// T-170 Fase 1d — polling di document_jobs (P3: niente Realtime).
// Poll su mount + intervallo finché esistono job attivi (queued/analyzing) +
// su appStateChange→active. Espone i job + un refetch manuale.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useIonViewWillEnter } from '@ionic/react';
import { App as CapacitorApp } from '@capacitor/app';
import { fetchDocumentJobs } from './documentJobsClient.js';
import { isActiveJobStatus, type DocumentJobRow } from '@trackeat/shared';

const POLL_MS = 4000;

export function useDocumentJobs(
  tenantId: string | null | undefined,
  businessId: string | null | undefined,
): { jobs: DocumentJobRow[]; loading: boolean; refetch: () => void } {
  const [jobs, setJobs] = useState<DocumentJobRow[]>([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    if (!tenantId || !businessId) {
      setJobs([]);
      return;
    }
    try {
      setLoading(true);
      const rows = await fetchDocumentJobs(tenantId, businessId);
      if (mounted.current) setJobs(rows);
    } catch {
      // silenzioso: il prossimo tick ritenta.
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [tenantId, businessId]);

  // Loop: ripianifica solo finché ci sono job attivi (altrimenti si ferma).
  const scheduleNext = useCallback(
    (hasActive: boolean) => {
      if (timer.current) clearTimeout(timer.current);
      if (!hasActive) return;
      timer.current = setTimeout(async () => {
        await load();
      }, POLL_MS);
    },
    [load],
  );

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [load]);

  useEffect(() => {
    scheduleNext(jobs.some((j) => isActiveJobStatus(j.status)));
  }, [jobs, scheduleNext]);

  // Refetch ogni volta che si ENTRA nella pagina Documenti (Ionic tiene le
  // pagine in cache: tornando da /documents/capture dopo l'invio, il componente
  // NON viene rimontato e il polling era già fermo perché non c'erano job attivi
  // → senza questo, un job appena accodato compariva solo dopo chiudi/riapri).
  useIonViewWillEnter(() => {
    void load();
  });

  // Refetch quando l'app torna attiva (riconciliazione post-background).
  useEffect(() => {
    const h = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) void load();
    });
    return () => {
      void h.then((l) => l.remove());
    };
  }, [load]);

  return { jobs, loading, refetch: () => void load() };
}
